import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import { IconErrorCircle, IconWarningTriangle } from "@mzc-cloudplex/icons";
import { Autocomplete as MuiAutocomplete, Checkbox, Chip, TextField } from "@mzc-pdc/ui";

export const AutocompleteWithObject = ({
                                           id,
                                           multiple = true,
                                           value,
                                           options,
                                           onClickOption,
                                           placeholder,
                                           error,
                                           helperText,
                                           ...rest
                                       }) => {
    const { t } = useTranslation();

    const defaultValue = multiple ? [] : null;
    const getEndAdornment = useMemo(() => {
        if (error) {
            if (!helperText) return <IconWarningTriangle color={"warning"} size={16} />;
            return <IconErrorCircle color={`error`} size={16} />;
        }
        return undefined;
    }, [error, helperText]);

    const isValueEmpty = useMemo(() => {
        if (multiple) {
            return value?.length === 0;
        }
        return !value;
    }, [multiple, value]);

    return (
        <MuiAutocomplete
            clearText={t(`common::label::Clear`, `Clear`)}
            openText={t(`common::label::Open`, `Open`)}
            closeText={t(`common::label::Close`, `Close`)}
            multiple={multiple}
            disableClearable={multiple}
            disableCloseOnSelect={multiple}
            value={value ?? defaultValue}
            options={options ?? []}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        placeholder={!isValueEmpty ? undefined : placeholder}
                        inputProps={{
                            ...params.inputProps,
                            value: params.inputProps?.value ?? "",
                            endAdornment: getEndAdornment,
                        }}
                    />
                );
            }}
            renderOption={(props, option) => (
                <li {...props}>
                    <>
                        {multiple && (
                            <Checkbox
                                color={"greySecondary"}
                                style={{ marginRight: 8 }}
                                checked={value?.find((x) => x?.value === option?.value) !== undefined}
                            />
                        )}
                        <>{option.label}</>
                    </>
                </li>
            )}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            renderTags={(value, getTagProps) => {
                return value.map((option, index) => (
                    <Chip
                        size={"small"}
                        label={t(`common::label::${option?.label}`, option?.label)}
                        {...getTagProps({
                            index,
                        })}
                        key={index}
                    />
                ));
            }}
            onChange={(e, newValue) => {
                if (multiple) {
                    if (newValue?.length === 0) newValue = [];
                    onClickOption(
                        id,
                        newValue?.reduce((acc, cur) => [...acc, cur], []),
                    );
                } else {
                    onClickOption(id, newValue);
                }
            }}
            {...rest}
        />
    );
};
